/**
 * A source's fetched jobs, as /api/external-jobs/sources/:id/jobs returns them (the service's
 * `_review_row`). CLOSED jobs are left out by the service unless asked for.
 */
export interface SourceJob {
  externalJobId: string;
  sourceJobCode?: string | null;
  title?: string | null;
  companyName?: string | null;
  brand?: string | null;
  locations?: { city?: string | null; state?: string | null; countryCode?: string | null; remote?: boolean }[];
  rawLocation?: string | null;
  remote?: boolean;
  pay?: { min?: number | null; max?: number | null; currency?: string | null; period?: string | null; text?: string | null } | null;
  postedAt?: string | null;
  applyUrl?: string | null;
  sourceUrl?: string | null;
  status?: string | null;
  filterReason?: string | null;
  enrichmentStatus?: string | null;
  skills?: string[];
  firstSeenAt?: string | null;
  lastSeenAt?: string | null;
}

export const JOBS_PAGE_SIZE = 25;

const CURRENCY_SYMBOL: Record<string, string> = { INR: '₹', USD: '$' };
const PERIOD_WORDS: Record<string, string> = {
  HOUR: 'an hour',
  DAY: 'a day',
  WEEK: 'a week',
  MONTH: 'a month',
  YEAR: 'a year',
};

/** "₹25,000 to ₹35,000 a month": pay as the listing states it, or "Not stated". Never inferred. */
export const formatPay = (pay: SourceJob['pay']): string => {
  if (!pay) return 'Not stated';
  const currency = (pay.currency || '').toUpperCase();
  const amount = (v: number) => {
    const n = v.toLocaleString(currency === 'INR' ? 'en-IN' : 'en-US', { maximumFractionDigits: 2 });
    return CURRENCY_SYMBOL[currency] ? `${CURRENCY_SYMBOL[currency]}${n}` : `${currency} ${n}`.trim();
  };
  const lo = typeof pay.min === 'number' && pay.min > 0 ? pay.min : null;
  const hi = typeof pay.max === 'number' && pay.max > 0 ? pay.max : null;
  if (lo === null && hi === null) return 'Not stated';
  const range =
    lo !== null && hi !== null && lo !== hi
      ? `${amount(lo)} to ${amount(hi)}`
      : lo !== null && hi !== null
        ? amount(lo)
        : lo !== null
          ? `From ${amount(lo)}`
          : `Up to ${amount(hi as number)}`;
  const period = PERIOD_WORDS[(pay.period || '').toUpperCase()];
  return period ? `${range} ${period}` : range;
};

/** "Mumbai, Maharashtra, IN +2 more", "Remote", or the source's own words when nothing resolved. */
export const jobLocation = (job: Pick<SourceJob, 'locations' | 'rawLocation' | 'remote'>): string => {
  const places = (job.locations ?? [])
    .map((l) => [l.city, l.state, l.countryCode].filter(Boolean).join(', ') || (l.remote ? 'Remote' : ''))
    .filter(Boolean);
  if (places.length === 0) return job.rawLocation || (job.remote ? 'Remote' : 'Not stated');
  return places.length > 1 ? `${places[0]} +${places.length - 1} more` : places[0];
};

/** "12 jobs", "1 job", or null until the count is known. */
export const jobsCount = (total: number | null): string | null =>
  total === null ? null : `${total.toLocaleString('en-IN')} ${total === 1 ? 'job' : 'jobs'}`;
